import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import PagesBrowser from '../../Custom/PagesBrowser';
import SensorPressureGraph from './SensorPressureGraph';
import UIDateFormater, { DateType } from '../../../containers/UIhelper/UIDateFormater';
import { buildPressureTraces } from './pressureTraceUtils';
import MultiDayTrendDialog from './MultiDayTrendDialog';
import { downloadCsv, formatCsvTimestamp, resolveCsvTimeZone } from '../../../utils/csvDownload';

const PRESSURE_CONVERSION = {
  meter: { factor: 1, label: 'Meter' },
  bar: { factor: 0.09804139432, label: 'Bar' },
  kpa: { factor: 9.80665, label: 'kPa' },
  psi: { factor: 1.42233, label: 'PSI' },
};

export default class PressureTabs extends React.Component {
  static get propTypes() {
    return {
      projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      loggerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      readingData: PropTypes.array,
      isFetching: PropTypes.bool,
      hoursBack: PropTypes.number,
      lastPressureSample: PropTypes.object,
      uiInfo: PropTypes.object,
      fetchReading: PropTypes.func,
      fetchTransients: PropTypes.func,
      fetchPressureSettings: PropTypes.func,
      timeZone: PropTypes.string,
      projectTimezone: PropTypes.string,
      pressureAlarms: PropTypes.array,
      transients: PropTypes.array,
      transientThresholds: PropTypes.object,
      fetchMultiDayReading: PropTypes.func,
      fetchMultiDayTransients: PropTypes.func,
      fetchPressureAlarmsForSensor: PropTypes.func,
      fetchTransientReading: PropTypes.func,
      pressureUnit: PropTypes.string,
      pressureSensors: PropTypes.array,
    };
  }

  constructor(props) {
    super(props);
    this.hasFetched = false;
    this.state = {
      multiDayDialogOpen: false,
    };
  }

  componentDidMount() {
    this.tryFetch();
  }

  componentDidUpdate(prevProps) {
    const { projectId, loggerId } = this.props;
    if (projectId !== prevProps.projectId || loggerId !== prevProps.loggerId) {
      this.hasFetched = false;
    }
    this.tryFetch();
  }

  tryFetch() {
    const { projectId, selectedFeature, loggerId, fetchReading, fetchTransients, fetchPressureSettings } = this.props;

    if (projectId && loggerId && !this.hasFetched) {
      this.hasFetched = true;
      fetchReading(projectId, selectedFeature, 0);
      fetchTransients(projectId, selectedFeature);
      if (fetchPressureSettings) fetchPressureSettings(projectId);
    }
  }

  onBrowsing = (direction) => {
    const { projectId, selectedFeature, hoursBack, fetchReading } = this.props;
    const step = 24;
    let newHoursBack = hoursBack;

    if (direction === 'prev') {
      newHoursBack += step;
    } else if (direction === 'next') {
      newHoursBack = Math.max(0, newHoursBack - step);
    }

    fetchReading(projectId, selectedFeature, newHoursBack);
  };

  getTitle = () => {
    const { hoursBack, timeZone, projectTimezone } = this.props;
    const now = new Date();
    const fromTime = now.getTime() - (hoursBack + 24) * 60 * 60 * 1000;
    const toTime = now.getTime() - hoursBack * 60 * 60 * 1000;

    return `${UIDateFormater(fromTime, DateType.DATE, timeZone, projectTimezone)} - ${UIDateFormater(toTime, DateType.DATE, timeZone, projectTimezone)}`;
  };

  parseReadingForPlotly = () => {
    const { readingData, timeZone, projectTimezone, pressureAlarms, transients, transientThresholds, hoursBack, lastPressureSample } = this.props;
    const now = new Date();
    const fromTime = now.getTime() - (hoursBack + 24) * 60 * 60 * 1000;
    const toTime = now.getTime() - hoursBack * 60 * 60 * 1000;

    return buildPressureTraces({
      readingData,
      timeZone,
      projectTimezone,
      pressureAlarms,
      transients,
      transientThresholds,
      lastPressureSample,
      fromTime,
      toTime,
      t: this.context.t,
    });
  };

  openMultiDayDialog = () => {
    this.setState({ multiDayDialogOpen: true });
  };

  closeMultiDayDialog = () => {
    this.setState({ multiDayDialogOpen: false });
  };

  handleDownloadCsv = () => {
    const { readingData, transients, pressureUnit, selectedFeature, timeZone, projectTimezone } = this.props;
    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
    const unitLabel = conversion.label.toLowerCase();
    const { ianaTimeZone, label: tzLabel } = resolveCsvTimeZone(timeZone, projectTimezone);

    const readingHeaders = [`timestamp (${tzLabel})`, `pressure_${unitLabel}`];
    const readingRows = (readingData || []).map((r) => [
      formatCsvTimestamp(r.time_utc, ianaTimeZone),
      r.measurement_m != null ? (parseFloat(r.measurement_m) * conversion.factor).toFixed(4) : '',
    ]);

    const fileBase = `pressure_trend_sensor_${selectedFeature || 'unknown'}_${new Date().toISOString().split('T')[0]}`;
    downloadCsv(`${fileBase}.csv`, readingHeaders, readingRows);

    if (transients && transients.length > 0) {
      const transientHeaders = [`event_time (${tzLabel})`, `min_${unitLabel}`, `max_${unitLabel}`];
      const transientRows = transients.map((t) => [
        formatCsvTimestamp(t.event_time, ianaTimeZone),
        t.min_reading != null ? (parseFloat(t.min_reading) * conversion.factor).toFixed(4) : '',
        t.max_reading != null ? (parseFloat(t.max_reading) * conversion.factor).toFixed(4) : '',
      ]);
      downloadCsv(`${fileBase}_transients.csv`, transientHeaders, transientRows);
    }
  };

  render() {
    const { hoursBack, isFetching, uiInfo, readingData, projectId, selectedFeature,
            pressureAlarms, transients, transientThresholds, lastPressureSample, timeZone, projectTimezone, fetchMultiDayReading,
            fetchMultiDayTransients, fetchPressureAlarmsForSensor, fetchTransientReading, pressureUnit, pressureSensors } = this.props;
    const { multiDayDialogOpen } = this.state;
    const plotData = this.parseReadingForPlotly();
    const title = this.getTitle();
    const hasData = readingData?.length > 0;

    return (
      <>
        <PagesBrowser nextDisabled={hoursBack <= 0} onBrowsing={this.onBrowsing}>
          {hasData ? (
            <SensorPressureGraph
              title={title}
              plotData={plotData}
              uiInfo={uiInfo}
              isFetching={isFetching}
              rangemode="normal"
              allowChangeUnits
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h4>{title}</h4>
              <p>{isFetching ? this.context.t('loading') : this.context.t('no_data')}</p>
            </div>
          )}
        </PagesBrowser>

        <div style={{ textAlign: 'left', marginTop: '8px' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={this.openMultiDayDialog}
          >
            {this.context.t('multi_day_trend')}
          </Button>
          <div style={{ marginTop: '6px' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={this.handleDownloadCsv}
              disabled={!hasData}
            >
              {`${this.context.t('download')} CSV`}
            </Button>
          </div>
        </div>

        <MultiDayTrendDialog
          open={multiDayDialogOpen}
          onClose={this.closeMultiDayDialog}
          projectId={projectId}
          sensorId={selectedFeature}
          pressureAlarms={pressureAlarms}
          lastPressureSample={lastPressureSample}
          transientThresholds={transientThresholds}
          timeZone={timeZone}
          projectTimezone={projectTimezone}
          uiInfo={uiInfo}
          fetchMultiDayReading={fetchMultiDayReading}
          fetchMultiDayTransients={fetchMultiDayTransients}
          fetchPressureAlarmsForSensor={fetchPressureAlarmsForSensor}
          fetchTransientReading={fetchTransientReading}
          pressureUnit={pressureUnit}
          pressureSensors={pressureSensors}
        />
      </>
    );
  }
}

PressureTabs.contextTypes = {
  t: PropTypes.func.isRequired,
};
